import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSidebarDropdown } from '../hooks/useSidebarDropdown';

const SubmenuItem = memo(({ subItem, isActive, handleItemClick, t, className }) => (
    <Link
        to={subItem.path || '#'}
        className={`submenu-item ${isActive ? 'active' : ''} ${className}`}
        onClick={handleItemClick}
    >
        <span>{t(subItem.translationKey)}</span>
    </Link>
));

SubmenuItem.displayName = 'SubmenuItem';

const SidebarDropdown = memo(({ item, isExpanded, isActive, onDropdownClick, onItemClick }) => {
    const { t } = useTranslation();
    const [expandedSections, setExpandedSections] = useState({});
    const { 
        isMobile,
        isHovering,
        isPathActive,
        handleClick,
        handleItemClick,
        handleMouseEnter,
        handleMouseLeave
    } = useSidebarDropdown({ isExpanded, onDropdownClick });

    const handleSectionClick = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const renderSubmenuItems = useMemo(() => (items) => {
        if (!Array.isArray(items)) return null;
        
        return (
            <div className="submenu-layout">
                {items.map((subItem) => {
                    const isPayslipItem = ['current-payslips', 'payslips-archive'].includes(subItem.id);
                    const specialItemClass = isPayslipItem ? 'payslip-item' : '';

                    if (subItem.type === 'nested-dropdown' || subItem.type === 'item-header') {
                        return (
                            <div key={subItem.id} className="submenu-section">
                                {subItem.type === 'item-header' ? (
                                    <Link
                                        to={subItem.path}
                                        className={`submenu-header ${location.pathname === subItem.path ? 'active' : ''}`}
                                        onClick={() => handleItemClick(subItem.path)}
                                        style={{ cursor: 'pointer', display: 'flex', textDecoration: 'none' }}
                                    >
                                        <span>{t(subItem.translationKey)}</span>
                                    </Link>
                                ) : (
                                    <>
                                        <div 
                                            className={`submenu-header ${isPathActive(subItem.items) ? 'active' : ''}`}
                                            onClick={() => handleSectionClick(subItem.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span>{t(subItem.translationKey)}</span>
                                            <i className={`bi bi-chevron-${expandedSections[subItem.id] ? 'up' : 'down'} ms-2`}></i>
                                        </div>
                                        <div className={`submenu-items ${expandedSections[subItem.id] ? 'show' : 'hide'}`}>
                                            {subItem.items && renderSubmenuItems(subItem.items)}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    } else if (subItem.type === 'item-dropdown') {
                        return (
                            <div key={subItem.id} className="submenu-section">
                                <div 
                                    className={`submenu-item-dropdown ${expandedSections[subItem.id] ? 'expanded' : ''} ${location.pathname === subItem.path ? 'active' : ''}`}
                                    onClick={() => handleSectionClick(subItem.id)}
                                >
                                    <span>{t(subItem.translationKey)}</span>
                                    <i className={`bi bi-chevron-${expandedSections[subItem.id] ? 'up' : 'down'} ms-2`}></i>
                                </div>
                                {expandedSections[subItem.id] && subItem.items && (
                                    <div className="submenu-items show">
                                        {renderSubmenuItems(subItem.items)}
                                    </div>
                                )}
                            </div>
                        );
                    } else if (subItem.type === 'nested-item') {
                        return (
                            <Link 
                                key={subItem.id}
                                to={subItem.path}
                                className={`nested-item ${specialItemClass}`}
                                onClick={() => handleItemClick(subItem.path)}
                            >
                                <span>{t(subItem.translationKey)}</span>
                            </Link>
                        );
                    } else {
                        return (
                            <SubmenuItem 
                                key={subItem.id}
                                subItem={subItem}
                                isActive={location.pathname === subItem.path}
                                handleItemClick={handleItemClick}
                                t={t}
                                className={specialItemClass}
                            />
                        );
                    }
                })}
            </div>
        );
    }, [t, handleItemClick, isPathActive, location.pathname, expandedSections, handleSectionClick]);

    return (
        <div 
            className={`sidebar-dropdown ${isActive || isHovering ? 'active' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button 
                className={`nav-link d-flex align-items-center justify-content-between w-100 ${isActive || isHovering ? 'active' : ''}`}
                onClick={handleClick}
            >
                <div className="d-flex align-items-center">
                    {/* <i className="bi bi-folder me-2"></i> */}
                    {isExpanded && <span className="text-truncate">{t(item.translationKey)}</span>}
                </div>
                {isExpanded && <i className="bi bi-chevron-right ms-auto"></i>}
            </button>

            <div className={`sidebar-submenu ${isActive || isHovering ? 'show' : ''}`}>
                <div className="submenu-content">
                    {renderSubmenuItems(item.items)}
                </div>
            </div>
        </div>
    );
});

SidebarDropdown.displayName = 'SidebarDropdown';
export default SidebarDropdown;
